import React, { useMemo } from 'react';
import { GoogleMap, LoadScript, Polygon, Marker } from '@react-google-maps/api';
import { Card } from './ui/card';
import {
  Eye, Package, CheckCircle, TrendingUp, AlertTriangle, MapPin,
  Briefcase, ExternalLink, FileText,
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Skip LoadScript when the Maps JS is already on the page (e.g. loaded by
// another admin view) — mounting a second LoadScript throws.
const MapsWrapper = ({ children }) => {
  if (typeof window !== 'undefined' && window.google?.maps) return children;
  return <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>{children}</LoadScript>;
};

// Shared job report view — rendered by BOTH the client portal
// (ClientPortal.jsx → ClientJobReport) and the admin mirror page
// (AdminClientReport.jsx), so the admin sees exactly what the customer sees.
export const JobReportView = ({ data, onBack, backLabel = 'Back to Reports', testId = 'client-job-report' }) => {
  const formatStatus = (s) => (s || '').replace(/_/g, ' ');
  const statusBadgeClass = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'completed' || v === 'paid' || v === 'approved') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (v === 'in_progress' || v === 'assigned') return 'bg-amber-100 text-amber-700 border-amber-200';
    if (v === 'open') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    if (v === 'partial_complete') return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Group curated photos by child_job_id so each child gets its own gallery.
  const photosByChild = useMemo(() => {
    const out = {};
    (data?.curated_photos || []).forEach((p) => {
      const key = p.child_job_id || '__parent__';
      if (!out[key]) out[key] = [];
      out[key].push(p);
    });
    return out;
  }, [data]);

  if (!data) return null;

  const { job, child_progress, curated_photos = [], has_curated_report, admin_updates = [] } = data;
  // Total houses delivered: prefer aggregated child stat, fall back to standalone progress.
  const housesDelivered = child_progress?.houses_delivered || 0;
  const hasChildren = (child_progress?.child_count || 0) > 0;
  // Percent completed = Houses Delivered / Deliveries (capped at 100).
  const percentCompleted = job.deliveries > 0
    ? Math.min(100, Math.round((housesDelivered / job.deliveries) * 100))
    : 0;
  // Admin-flagged Non Deliverable + Gated Community houses (parent + children).
  const attemptedNonDeliverable = data.attempted_non_deliverable || 0;

  // Build map center: prefer job.map_center; fall back to first polygon point.
  let mapCenter = null;
  if (Array.isArray(job.map_center) && job.map_center.length === 2) mapCenter = { lat: job.map_center[0], lng: job.map_center[1] };
  else if (Array.isArray(job.delivery_boundary) && job.delivery_boundary.length > 0) {
    const [lat, lng] = job.delivery_boundary[0];
    mapCenter = { lat, lng };
  }

  return (
    <div className="space-y-6" data-testid={testId}>
      {/* Back button */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-purple-600 hover:text-purple-700 text-sm font-medium inline-flex items-center"
          data-testid="back-to-reports"
        >
          &larr; {backLabel}
        </button>
      )}

      {/* Job Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm text-gray-500 flex items-center gap-1">
            <Eye size={14} className="text-purple-600" />
            Job Details
          </p>
          <h2 className="text-2xl font-bold text-gray-900">
            {job.job_number && <span className="text-blue-600 mr-1">#{String(job.job_number).padStart(4, '0')}</span>}
            {job.title}
          </h2>
          {(job.client || job.location) && (
            <p className="text-gray-600 text-sm">
              {job.client}{job.client && job.location ? ' • ' : ''}{job.location}
            </p>
          )}
        </div>
        <span className={`text-xs uppercase tracking-wide px-2 py-1 rounded border ${statusBadgeClass(job.status)}`}>
          {formatStatus(job.status)}
        </span>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-orange-50 rounded-xl p-4 text-center">
          <Package size={22} className="mx-auto text-orange-500 mb-1.5" />
          <p className="text-2xl font-bold text-gray-900" data-testid="stat-deliveries">{job.deliveries}</p>
          <p className="text-xs text-gray-500">Deliveries</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4 text-center">
          <CheckCircle size={22} className="mx-auto text-emerald-500 mb-1.5" />
          <p className="text-2xl font-bold text-emerald-600" data-testid="stat-houses-delivered">{housesDelivered}</p>
          <p className="text-xs text-gray-500">Houses Delivered</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <TrendingUp size={22} className="mx-auto text-blue-500 mb-1.5" />
          <p className="text-2xl font-bold text-blue-600" data-testid="stat-percent-completed">{percentCompleted}%</p>
          <p className="text-xs text-gray-500">Percent Completed</p>
        </div>
        <div className="bg-red-50 rounded-xl p-4 text-center">
          <AlertTriangle size={22} className="mx-auto text-red-700 mb-1.5" />
          <p className="text-2xl font-bold text-red-800" data-testid="stat-attempted-non-deliverable">{attemptedNonDeliverable}</p>
          <p className="text-xs text-gray-500">Attempted Non Deliverables</p>
        </div>
      </div>

      {/* Delivery Zone Map */}
      {(job.delivery_boundary || []).length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <MapPin size={18} className="text-purple-600" />
            Delivery Zone
          </h3>
          <div className="h-[500px] rounded-xl overflow-hidden border" data-testid="report-map">
            {mapCenter && GOOGLE_MAPS_API_KEY ? (
              <MapsWrapper>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={mapCenter}
                  zoom={12}
                  options={{ disableDefaultUI: true, zoomControl: true, mapTypeControl: true }}
                >
                  <Polygon
                    paths={(job.delivery_boundary || []).map(([lat, lng]) => ({ lat, lng }))}
                    options={{ fillColor: '#8b5cf6', fillOpacity: 0.18, strokeColor: '#7c3aed', strokeWeight: 2 }}
                  />
                  {(child_progress?.children || []).map((child, idx) => {
                    if (!Array.isArray(child.delivery_boundary) || child.delivery_boundary.length < 3) return null;
                    const palette = ['#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#a855f7', '#f97316', '#22c55e'];
                    const color = palette[idx % palette.length];
                    // Small grid label (e.g. "A1") pinned at the zone's center.
                    const label = child.grid_label || '';
                    let center = null;
                    if (Array.isArray(child.map_center) && child.map_center.length >= 2) {
                      center = { lat: child.map_center[0], lng: child.map_center[1] };
                    } else {
                      const pts = child.delivery_boundary;
                      center = {
                        lat: pts.reduce((s, p) => s + p[0], 0) / pts.length,
                        lng: pts.reduce((s, p) => s + p[1], 0) / pts.length,
                      };
                    }
                    return (
                      <React.Fragment key={child.id}>
                        <Polygon
                          paths={child.delivery_boundary.map(([lat, lng]) => ({ lat, lng }))}
                          options={{ fillColor: color, fillOpacity: 0.25, strokeColor: color, strokeWeight: 1.5 }}
                        />
                        {label && center && (() => {
                          const w = Math.max(26, 14 + label.length * 7);
                          return (
                            <Marker
                              position={center}
                              icon={{
                                url: `data:image/svg+xml,${encodeURIComponent(`
                                  <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="18">
                                    <rect x="0.5" y="0.5" width="${w - 1}" height="17" rx="5" fill="white" fill-opacity="0.92" stroke="${color}" stroke-width="1"/>
                                    <text x="${w / 2}" y="13" font-family="Arial" font-size="11" font-weight="bold" fill="#111827" text-anchor="middle">${label}</text>
                                  </svg>
                                `)}`,
                                scaledSize: { width: w, height: 18 },
                                anchor: { x: w / 2, y: 9 },
                              }}
                              clickable={false}
                              zIndex={70}
                            />
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
                </GoogleMap>
              </MapsWrapper>
            ) : (
              <div className="h-full w-full bg-gray-50 flex items-center justify-center text-sm text-gray-400">
                Map not available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Child Jobs Overview — only renders when parent is split */}
      {hasChildren && (
        <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200" data-testid="child-jobs-overview">
          <h3 className="font-semibold text-indigo-900 flex items-center gap-2 mb-2">
            <Briefcase size={18} className="text-indigo-600" />
            Job — Zone Overview
          </h3>
          <p className="text-sm text-indigo-700 mb-3">
            This job has been split into delivery zones. Progress from every zone is aggregated below.
          </p>
          <div className="flex items-center gap-4 bg-white rounded-lg p-3 border border-indigo-100 mb-3">
            <div className="text-center flex-1">
              <p className="text-2xl font-bold text-indigo-700">{child_progress.child_count}</p>
              <p className="text-xs text-gray-500">Zones</p>
            </div>
            <div className="h-10 w-px bg-indigo-200" />
            <div className="text-center flex-1">
              <p className="text-2xl font-bold text-emerald-600">{child_progress.houses_delivered}</p>
              <p className="text-xs text-gray-500">Houses Delivered</p>
            </div>
            <div className="h-10 w-px bg-indigo-200" />
            <div className="text-center flex-1">
              <p className="text-2xl font-bold text-blue-600">{child_progress.gps_points}</p>
              <p className="text-xs text-gray-500">GPS Points</p>
            </div>
          </div>
          {/* Child rows — clicking opens that zone's live delivery tracking
              page in a new tab (per user request Mar 2026). */}
          <div className="space-y-2">
            {(child_progress.children || []).map((child) => (
              <button
                type="button"
                key={child.id}
                onClick={() => window.open(`/track/${child.id}`, '_blank', 'noopener')}
                title={`Open live delivery tracking for ${child.title}`}
                className="w-full flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-indigo-100 text-sm hover:bg-indigo-50 hover:border-indigo-300 transition-colors text-left cursor-pointer"
                data-testid={`child-row-${child.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${statusBadgeClass(child.status)}`}>
                    {formatStatus(child.status)}
                  </span>
                  <span className="font-medium text-gray-800 truncate">{child.title}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-500 shrink-0">
                  <span>{child.delivered_count}/{child.deliveries} delivered</span>
                  {child.route_points > 0 && <span className="hidden sm:inline">{child.route_points} GPS pts</span>}
                  <ExternalLink size={14} className="text-indigo-400" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Delivery Progress + Photos */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Eye size={18} className="text-purple-600" />
          Delivery Progress & Photos
        </h3>
        {!has_curated_report && admin_updates.length === 0 ? (
          <Card className="p-8 border-0 shadow-sm text-center">
            <FileText size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-400 text-sm">No photos selected by the team yet.</p>
            <p className="text-xs text-gray-300 mt-1">
              Once your delivery team publishes a report with curated photos, they&apos;ll appear here.
            </p>
          </Card>
        ) : !has_curated_report ? null : curated_photos.length === 0 ? (
          <Card className="p-8 border-0 shadow-sm text-center">
            <FileText size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-400 text-sm">No photos were attached to this report.</p>
          </Card>
        ) : hasChildren ? (
          // Per-child photo galleries — each section scroll-targeted by child id
          <div className="space-y-4">
            {(child_progress.children || []).map((child) => {
              const photos = photosByChild[child.id] || [];
              return (
                <div
                  key={child.id}
                  className="bg-white rounded-xl border border-gray-200 p-4"
                  data-testid={`child-section-${child.id}`}
                >
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${statusBadgeClass(child.status)}`}>
                        {formatStatus(child.status)}
                      </span>
                      <p className="font-medium text-gray-800">{child.title}</p>
                    </div>
                    <p className="text-xs text-gray-500">{child.delivered_count}/{child.deliveries} delivered</p>
                  </div>
                  {photos.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No curated photos for this zone.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {photos.map((p) => (
                        <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="block">
                          <img
                            src={p.url}
                            alt="Delivery"
                            loading="lazy"
                            className="rounded-md w-full aspect-square object-cover border border-gray-200 hover:opacity-90 transition-opacity"
                            data-testid={`curated-photo-${p.id}`}
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          // Standalone parent — one combined gallery
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {curated_photos.map((p) => (
              <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={p.url}
                  alt="Delivery"
                  loading="lazy"
                  className="rounded-md w-full aspect-square object-cover border border-gray-200 hover:opacity-90 transition-opacity"
                  data-testid={`curated-photo-${p.id}`}
                />
              </a>
            ))}
          </div>
        )}

        {/* Admin Updates feed — appended under the curated photo galleries
            above. Each card is one "Daily Update and Photos Report" the
            admin published against this parent (or any of its children).
            Newest first. Empty list → nothing renders. */}
        {admin_updates && admin_updates.length > 0 && (
          <div className="mt-6 space-y-4" data-testid="admin-updates-feed">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
              <Eye size={16} className="text-purple-600" />
              Daily Updates from the Team
              <span className="text-xs font-normal text-gray-400">
                ({admin_updates.length})
              </span>
            </h4>
            {admin_updates.map((u) => {
              const when = u.published_at || u.created_at;
              const whenLabel = when ? new Date(when).toLocaleString(undefined, {
                month: 'short', day: 'numeric', year: 'numeric',
                hour: 'numeric', minute: '2-digit',
              }) : '';
              return (
                <div
                  key={u.id}
                  className="bg-white rounded-xl border border-gray-200 p-4"
                  data-testid={`admin-update-${u.id}`}
                >
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200">
                        Update
                      </span>
                      {whenLabel && (
                        <p className="text-xs text-gray-500" data-testid={`admin-update-when-${u.id}`}>
                          {whenLabel}
                        </p>
                      )}
                    </div>
                    {u.created_by_name && (
                      <p className="text-xs text-gray-400">By {u.created_by_name}</p>
                    )}
                  </div>
                  {u.summary && (
                    <p
                      className="text-sm text-gray-700 whitespace-pre-wrap mb-3"
                      data-testid={`admin-update-summary-${u.id}`}
                    >
                      {u.summary}
                    </p>
                  )}
                  {u.photos && u.photos.length > 0 && (
                    <div
                      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
                      data-testid={`admin-update-photos-${u.id}`}
                    >
                      {u.photos.map((p) => (
                        <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="block">
                          <img
                            src={p.url}
                            alt="Update"
                            loading="lazy"
                            className="rounded-md w-full aspect-square object-cover border border-gray-200 hover:opacity-90 transition-opacity"
                            data-testid={`admin-update-photo-${p.id}`}
                          />
                        </a>
                      ))}
                    </div>
                  )}
                  {(!u.summary && (!u.photos || u.photos.length === 0)) && (
                    <p className="text-xs text-gray-400 italic">No notes or photos attached.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobReportView;
